import { api } from '@/lib/api'
import type { AppRequestConfig } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import {
  ApiResponse,
  JobCategory,
  JobCategoryCreatePayload,
  JobCategoryUpdatePayload,
  PaginatedResponse,
} from '@/types'

export type CategoryKind = 'job' | 'project' | 'event' | 'committee'

type JobCategoryImageOptions = {
  logoFile?: File | null
  removeLogo?: boolean
}

type CategoryQueryParams = {
  include_inactive?: boolean
  kind?: CategoryKind
  service_instance_id?: string
  local_only?: boolean
  page?: number
  page_size?: number
}

type CategoryMutationOptions = JobCategoryImageOptions & {
  kind?: CategoryKind
  scope?: 'local' | 'org'
  service_instance_id?: string
  serviceInstanceHeader?: string
  skipPostIdHeader?: boolean
}

type CategoryRequestOptions = {
  serviceInstanceHeader?: string
  skipPostIdHeader?: boolean
}

const CATEGORY_PATHS = API_PATHS.categories

const resolveKindedCategoryPaths = (kind: CategoryKind = 'job') => {
  if (kind === 'project') {
    return {
      list: CATEGORY_PATHS.projectList,
      create: CATEGORY_PATHS.projectCreate,
      detail: CATEGORY_PATHS.projectDetail,
      update: CATEGORY_PATHS.projectUpdate,
      deactivate: CATEGORY_PATHS.projectDeactivate,
      activate: CATEGORY_PATHS.projectActivate,
      delete: CATEGORY_PATHS.projectDelete,
    }
  }

  if (kind === 'committee') {
    return {
      list: CATEGORY_PATHS.committeeList,
      create: CATEGORY_PATHS.committeeCreate,
      detail: CATEGORY_PATHS.committeeDetail,
      update: CATEGORY_PATHS.committeeUpdate,
      deactivate: CATEGORY_PATHS.committeeDeactivate,
      activate: CATEGORY_PATHS.committeeActivate,
      delete: CATEGORY_PATHS.committeeDelete,
    }
  }

  if (kind === 'event') {
    return {
      list: CATEGORY_PATHS.eventList,
      create: CATEGORY_PATHS.eventCreate,
      detail: CATEGORY_PATHS.eventDetail,
      update: CATEGORY_PATHS.eventUpdate,
      deactivate: CATEGORY_PATHS.eventDeactivate,
      activate: CATEGORY_PATHS.eventActivate,
      delete: CATEGORY_PATHS.eventDelete,
    }
  }

  return {
    list: CATEGORY_PATHS.list,
    create: CATEGORY_PATHS.create,
    detail: CATEGORY_PATHS.detail,
    update: CATEGORY_PATHS.update,
    deactivate: CATEGORY_PATHS.deactivate,
    activate: CATEGORY_PATHS.activate,
    delete: CATEGORY_PATHS.delete,
  }
}

function toCategoryFormData(payload: JobCategoryCreatePayload | JobCategoryUpdatePayload, options?: JobCategoryImageOptions) {
  const formData = new FormData()
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, String(value))
    }
  })
  if (options?.logoFile) {
    formData.append('logo_file', options.logoFile)
  }
  if (options?.removeLogo) {
    formData.append('remove_logo', 'true')
  }
  return formData
}

export const categoriesApi = {
  getCategories: async (
    params?: CategoryQueryParams,
    requestOptions?: CategoryRequestOptions,
  ): Promise<PaginatedResponse<JobCategory>> => {
    const kind = params?.kind ?? 'job'
    const paths = resolveKindedCategoryPaths(kind)
    const queryParams: Record<string, unknown> = { kind }
    if (params?.include_inactive) {
      queryParams.include_inactive = true
    }
    if (params?.service_instance_id) {
      queryParams.service_instance_id = params.service_instance_id
    }
    if (params?.local_only) {
      queryParams.local_only = true
    }
    if (params?.page) {
      queryParams.page = params.page
    }
    if (params?.page_size) {
      queryParams.page_size = params.page_size
    }

    const requestConfig: AppRequestConfig = {
      params: Object.keys(queryParams).length ? queryParams : undefined,
      skipPostIdHeader: requestOptions?.skipPostIdHeader === true,
    }
    const explicitServiceHeader = String(requestOptions?.serviceInstanceHeader ?? '').trim()
    if (explicitServiceHeader) {
      requestConfig.headers = {
        'service-instance-id': explicitServiceHeader,
      }
    }

    const response = await api.get<ApiResponse<PaginatedResponse<JobCategory>> | PaginatedResponse<JobCategory>>(
      paths.list,
      requestConfig,
    )
    return 'data' in response.data ? response.data.data : response.data
  },

  getCategory: async (id: string, kind: CategoryKind = 'job'): Promise<JobCategory> => {
    const paths = resolveKindedCategoryPaths(kind)
    const response = await api.get<ApiResponse<JobCategory>>(paths.detail(id))
    return response.data.data
  },

  createCategory: async (
    payload: JobCategoryCreatePayload,
    options?: CategoryMutationOptions,
  ): Promise<JobCategory> => {
    const kind = options?.kind ?? payload.kind ?? 'job'
    const paths = resolveKindedCategoryPaths(kind)
    const requestBody = options?.logoFile
      ? toCategoryFormData({ ...payload, kind }, options)
      : { ...payload, kind }
    const mutationConfig: AppRequestConfig = {
      skipPostIdHeader: options?.skipPostIdHeader === true,
      params: {
        ...(options?.scope ? { scope: options.scope } : {}),
        ...(options?.service_instance_id ? { service_instance_id: options.service_instance_id } : {}),
      },
    }
    const explicitServiceHeader = String(options?.serviceInstanceHeader ?? '').trim()
    if (explicitServiceHeader) {
      mutationConfig.headers = {
        'service-instance-id': explicitServiceHeader,
      }
    }

    const response = await api.post<ApiResponse<JobCategory>>(
      paths.create,
      requestBody,
      requestBody instanceof FormData
        ? {
          ...mutationConfig,
          headers: {
            ...(mutationConfig.headers ?? {}),
            'Content-Type': 'multipart/form-data',
          },
        }
        : mutationConfig,
    )
    return response.data.data
  },

  updateCategory: async (
    id: string,
    payload: JobCategoryUpdatePayload,
    options?: CategoryMutationOptions,
  ): Promise<JobCategory> => {
    const kind = options?.kind ?? payload.kind ?? 'job'
    const paths = resolveKindedCategoryPaths(kind)
    const shouldUseFormData = Boolean(options?.logoFile || options?.removeLogo)
    const requestBody = shouldUseFormData ? toCategoryFormData(payload, options) : payload
    const mutationConfig: AppRequestConfig = {
      skipPostIdHeader: options?.skipPostIdHeader === true,
      params: {
        ...(options?.scope ? { scope: options.scope } : {}),
        ...(options?.service_instance_id ? { service_instance_id: options.service_instance_id } : {}),
      },
    }
    const explicitServiceHeader = String(options?.serviceInstanceHeader ?? '').trim()
    if (explicitServiceHeader) {
      mutationConfig.headers = {
        'service-instance-id': explicitServiceHeader,
      }
    }

    const response = await api.post<ApiResponse<JobCategory>>(
      paths.update(id),
      requestBody,
      requestBody instanceof FormData
        ? {
          ...mutationConfig,
          headers: {
            ...(mutationConfig.headers ?? {}),
            'Content-Type': 'multipart/form-data',
          },
        }
        : mutationConfig,
    )
    return response.data.data
  },

  deactivateCategory: async (id: string, kind: CategoryKind = 'job'): Promise<JobCategory> => {
    const paths = resolveKindedCategoryPaths(kind)
    const response = await api.post<ApiResponse<JobCategory>>(paths.deactivate(id))
    return response.data.data
  },

  activateCategory: async (id: string, kind: CategoryKind = 'job'): Promise<JobCategory> => {
    const paths = resolveKindedCategoryPaths(kind)
    const response = await api.post<ApiResponse<JobCategory>>(paths.activate(id))
    return response.data.data
  },

  deleteCategory: async (id: string, kind: CategoryKind = 'job'): Promise<void> => {
    const paths = resolveKindedCategoryPaths(kind)
    await api.delete(paths.delete(id))
  },
}

export default categoriesApi
